import { format } from "date-fns";
import moment from "moment";
import intl from "react-intl-universal";
import {
  downloadCsv,
  formatFilterAmount,
  formatFilterDate,
  parseAmountInput,
} from "../../../Components/Molecules/GridHistory/gridFilterUtils";

export const EMPTY_TICKETS_FILTERS = {
  dateFrom: null,
  dateTo: null,
  dueDateFrom: null,
  dueDateTo: null,
  amountMin: "",
  amountMax: "",
  status: "",
  document: "",
  name: "",
  anticipated: "",
  searchQuery: "",
};

const TICKET_STATUS_OPTIONS = [
  { id: "opened", labelKey: "tickets.statusOpened" },
  { id: "paid", labelKey: "tickets.statusPaid" },
  { id: "canceled", labelKey: "tickets.statusCanceled" },
  { id: "expired", labelKey: "tickets.statusExpired" },
];

const TICKET_ANTICIPATED_OPTIONS = [
  { id: "yes", labelKey: "tickets.yes" },
  { id: "no", labelKey: "tickets.no" },
];

const parseApiDate = (value) => {
  if (!value) return null;
  return new Date(String(value).replace(/-/g, "/").replace(/T.+/, ""));
};

const amountToApiCents = (value) => {
  const parsed = parseAmountInput(value);
  return parsed === null ? "" : String(Math.round(parsed * 100));
};

const amountFromApiCents = (value) => {
  if (!value && value !== 0) return "";
  return String(Number(value) / 100).replace(".", ",");
};

export const getTicketStatusOptions = () =>
  TICKET_STATUS_OPTIONS.map((option) => ({
    id: option.id,
    label: intl.get(option.labelKey),
  }));

export const getTicketAnticipatedOptions = () =>
  TICKET_ANTICIPATED_OPTIONS.map((option) => ({
    id: option.id,
    label: intl.get(option.labelKey),
  }));

export const getTicketStatusLabel = (statusId) => {
  const option = TICKET_STATUS_OPTIONS.find((item) => item.id === statusId);
  return option ? intl.get(option.labelKey) : statusId;
};

export const filtersFromTicketsFilterObj = (filterObj = {}) => {
  let anticipated = "";
  if (filterObj.isAnticipation === true || filterObj.isAnticipation === "true") {
    anticipated = "yes";
  } else if (filterObj.isAnticipation === false || filterObj.isAnticipation === "false") {
    anticipated = "no";
  }

  return {
    dateFrom: parseApiDate(filterObj.start_date),
    dateTo: parseApiDate(filterObj.end_date),
    dueDateFrom: parseApiDate(filterObj.due_date_start),
    dueDateTo: parseApiDate(filterObj.due_date_end),
    amountMin: amountFromApiCents(filterObj.start_amount),
    amountMax: amountFromApiCents(filterObj.end_amount),
    status: filterObj.type || "",
    document: filterObj.document || "",
    name: filterObj.name || "",
    anticipated,
    searchQuery: "",
  };
};

export const ticketsFilterObjFromFilters = (filters = EMPTY_TICKETS_FILTERS) => {
  const next = {
    start_date: filters.dateFrom ? format(filters.dateFrom, "yyyy-MM-dd") : "",
    end_date: filters.dateTo ? format(filters.dateTo, "yyyy-MM-dd") : "",
    due_date_start: filters.dueDateFrom ? format(filters.dueDateFrom, "yyyy-MM-dd") : "",
    due_date_end: filters.dueDateTo ? format(filters.dueDateTo, "yyyy-MM-dd") : "",
    start_amount: amountToApiCents(filters.amountMin),
    end_amount: amountToApiCents(filters.amountMax),
    document: filters.document?.trim() || "",
    name: filters.name?.trim() || "",
    type: filters.status || "",
    isAnticipation: "",
  };

  if (filters.anticipated === "yes") next.isAnticipation = true;
  if (filters.anticipated === "no") next.isAnticipation = false;

  return next;
};

export const hasActiveTicketsFilters = (filters) =>
  Boolean(filters.dateFrom)
  || Boolean(filters.dateTo)
  || Boolean(filters.dueDateFrom)
  || Boolean(filters.dueDateTo)
  || filters.amountMin !== ""
  || filters.amountMax !== ""
  || Boolean(filters.status)
  || Boolean(filters.document?.trim())
  || Boolean(filters.name?.trim())
  || Boolean(filters.anticipated)
  || Boolean(filters.searchQuery?.trim());

export const isTicketsChipFilterActive = (filters, chipId) => {
  switch (chipId) {
    case "date":
      return Boolean(filters.dateFrom || filters.dateTo);
    case "amount":
      return filters.amountMin !== "" || filters.amountMax !== "";
    case "status":
      return Boolean(filters.status);
    case "more":
      return Boolean(
        filters.dueDateFrom
        || filters.dueDateTo
        || filters.document?.trim()
        || filters.name?.trim()
        || filters.anticipated,
      );
    default:
      return false;
  }
};

export const buildTicketsFilterTags = (filters) => {
  const tags = [];

  if (filters.searchQuery?.trim()) {
    tags.push({
      key: "searchQuery",
      iconType: "search",
      value: filters.searchQuery.trim(),
    });
  }

  if (filters.dateFrom) {
    tags.push({
      key: "dateFrom",
      iconType: "date",
      value: formatFilterDate(filters.dateFrom),
    });
  }

  if (filters.dateTo) {
    tags.push({
      key: "dateTo",
      iconType: "date",
      value: formatFilterDate(filters.dateTo),
    });
  }

  if (filters.dueDateFrom) {
    tags.push({
      key: "dueDateFrom",
      iconType: "date",
      value: `${intl.get("tickets.filterDueDate")}: ${formatFilterDate(filters.dueDateFrom)}`,
    });
  }

  if (filters.dueDateTo) {
    tags.push({
      key: "dueDateTo",
      iconType: "date",
      value: `${intl.get("tickets.filterDueDate")}: ${formatFilterDate(filters.dueDateTo)}`,
    });
  }

  if (filters.amountMin !== "") {
    tags.push({
      key: "amountMin",
      iconType: "amount",
      value: formatFilterAmount(filters.amountMin),
    });
  }

  if (filters.amountMax !== "") {
    tags.push({
      key: "amountMax",
      iconType: "amount",
      value: formatFilterAmount(filters.amountMax),
    });
  }

  if (filters.status) {
    tags.push({
      key: "status",
      iconType: "status",
      value: getTicketStatusLabel(filters.status),
    });
  }

  if (filters.name?.trim()) {
    tags.push({
      key: "name",
      iconType: "search",
      value: filters.name.trim(),
    });
  }

  if (filters.document?.trim()) {
    tags.push({
      key: "document",
      iconType: "external",
      value: filters.document.trim(),
    });
  }

  if (filters.anticipated) {
    tags.push({
      key: "anticipated",
      iconType: "type",
      value: `${intl.get("tickets.filterAnticipated")}: ${
        filters.anticipated === "yes" ? intl.get("tickets.yes") : intl.get("tickets.no")
      }`,
    });
  }

  return tags;
};

export const removeTicketsFilterTag = (filters, tagKey) => {
  switch (tagKey) {
    case "dateFrom":
      return { ...filters, dateFrom: null };
    case "dateTo":
      return { ...filters, dateTo: null };
    case "dueDateFrom":
      return { ...filters, dueDateFrom: null };
    case "dueDateTo":
      return { ...filters, dueDateTo: null };
    case "amountMin":
      return { ...filters, amountMin: "" };
    case "amountMax":
      return { ...filters, amountMax: "" };
    case "status":
      return { ...filters, status: "" };
    case "name":
      return { ...filters, name: "" };
    case "document":
      return { ...filters, document: "" };
    case "anticipated":
      return { ...filters, anticipated: "" };
    case "searchQuery":
      return { ...filters, searchQuery: "" };
    default:
      return filters;
  }
};

export const applyTicketsClientFilters = (items, filters) => {
  if (!filters?.searchQuery?.trim()) return items;

  const query = filters.searchQuery.trim().toLowerCase();
  return (items ?? []).filter((item) => {
    const haystack = [
      item?.payer?.name,
      item?.payer?.document,
      item?.status,
      item?.id,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return haystack.includes(query);
  });
};

export const exportTicketsCsv = (items) => {
  const headers = [
    intl.get("tickets.payer"),
    intl.get("tickets.document"),
    intl.get("tickets.emission"),
    intl.get("tickets.dueDate"),
    intl.get("tickets.amount"),
    intl.get("tickets.payment"),
    intl.get("tickets.amountPaid"),
    intl.get("tickets.anticipated"),
    intl.get("tickets.status"),
  ];

  const rows = (items ?? []).map((item) => [
    item?.payer?.name || "",
    item?.payer?.document || "",
    item?.created_at ? moment(item.created_at).format("DD/MM/YYYY") : "",
    item?.expiration ? moment(item.expiration).format("DD/MM/YYYY") : "",
    String(Number(item?.amount ?? 0)).replace(".", ","),
    item?.payment_at ? moment(item.payment_at).format("DD/MM/YYYY") : "",
    item?.amount_paid == null || item?.amount_paid === 0
      ? ""
      : String(Number(item.amount_paid)).replace(".", ","),
    item?.anticipated ? intl.get("tickets.yes") : intl.get("tickets.no"),
    item?.status || "",
  ]);

  downloadCsv(headers, rows, `boletos-${format(new Date(), "yyyy-MM-dd")}.csv`);
};
